import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

interface DeleteButtonProps {
  onDelete: () => Promise<boolean>;
  entityType: 'camera' | 'chamber';
  entityId: string;
  disabled?: boolean;
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  entityType,
  entityId,
  disabled = false,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const success = await onDelete();
      if (success) {
        setOpen(false);
      } else {
        // Handle error - could show a toast notification here
        console.error(`Failed to delete ${entityType} ${entityId}`);
      }
    } catch (error) {
      console.error(`Error deleting ${entityType} ${entityId}:`, error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Tooltip title={disabled ? `Delete ${entityType} (enabled only for disconnected devices)` : `Delete ${entityType}`}>
        <span>
          <IconButton
            color="error"
            size="small"
            disabled={disabled || isDeleting}
            onClick={() => setOpen(true)}
            sx={{
              opacity: disabled ? 0.5 : 1,
              '&:hover': {
                opacity: disabled ? 0.5 : 0.8,
              }
            }}
          >
            <DeleteIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </span>
      </Tooltip>
      
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete {entityType}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to permanently delete this {entityType} ({entityId})? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            disabled={isDeleting}
            color="error"
            variant="contained"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 